// ./src/components/features/Reminders/RemindersFilterBar/RemindersFilterBar.tsx
//
// Filter controls for the event Reminders list: by kind, delivery channel, and status (active/paused).
// Fully controlled — the page owns the value and maps it to a ReminderFilter. Name search lives in the
// page header, not here. `vertical` stacks the controls for the mobile filter drawer.

import React from "react";
import { Group, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";

import { REMINDER_KIND, REMINDER_CHANNEL } from "@/utils/api";
import { MultiSelectField } from "@/components/generic";

export interface RemindersFilterValue {
  kinds: string[];
  channels: string[];
  statuses: number[];
}

export interface RemindersFilterBarProps {
  value: RemindersFilterValue;
  onChange: (value: RemindersFilterValue) => void;
  disabled?: boolean;
  vertical?: boolean;
}

export const EMPTY_REMINDERS_FILTER: RemindersFilterValue = {
  kinds: [],
  channels: [],
  statuses: [],
};

export const remindersFilterActiveCount = (value: RemindersFilterValue): number =>
  (value.kinds.length > 0 ? 1 : 0) + (value.channels.length > 0 ? 1 : 0) + (value.statuses.length > 0 ? 1 : 0);

const CONTROL_STYLE: React.CSSProperties = { flex: "1 1 200px", minWidth: 180 };

const RemindersFilterBarComponent: React.FC<RemindersFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  vertical = false,
}) => {
  const { t } = useTranslation(["events"]);

  const kindData = [
    { value: REMINDER_KIND.EVENT_APPROACHING, label: t("events:reminders.kind.eventApproaching", { defaultValue: "Event approaching" }) },
    { value: REMINDER_KIND.RSVP_NO_RESPONSE, label: t("events:reminders.kind.rsvpNoResponse", { defaultValue: "RSVP nudge" }) },
  ];

  const channelData = [
    { value: REMINDER_CHANNEL.EMAIL, label: t("events:reminders.channel.email", { defaultValue: "Email" }) },
    { value: REMINDER_CHANNEL.SMS, label: t("events:reminders.channel.sms", { defaultValue: "SMS" }) },
  ];

  const statusData = [
    { value: String(STATUSES.ACTIVE), label: t("events:reminders.status.active", { defaultValue: "Active" }) },
    { value: String(STATUSES.DISABLED), label: t("events:reminders.status.paused", { defaultValue: "Paused" }) },
  ];

  const kindControl = (
    <MultiSelectField
      label={t("events:reminders.filters.kind.label", { defaultValue: "Kind" })}
      placeholder={t("events:reminders.filters.kind.placeholder", { defaultValue: "Any kind" })}
      data={kindData}
      value={value.kinds}
      onChange={(_name, kinds) => onChange({ ...value, kinds })}
      disabled={disabled}
      clearable
      size="md"
      style={vertical ? undefined : CONTROL_STYLE}
    />
  );

  const channelControl = (
    <MultiSelectField
      label={t("events:reminders.filters.channel.label", { defaultValue: "Channel" })}
      placeholder={t("events:reminders.filters.channel.placeholder", { defaultValue: "Any channel" })}
      data={channelData}
      value={value.channels}
      onChange={(_name, channels) => onChange({ ...value, channels })}
      disabled={disabled}
      clearable
      size="md"
      style={vertical ? undefined : CONTROL_STYLE}
    />
  );

  const statusControl = (
    <MultiSelectField
      label={t("events:reminders.filters.status.label", { defaultValue: "Status" })}
      placeholder={t("events:reminders.filters.status.placeholder", { defaultValue: "Any status" })}
      data={statusData}
      value={value.statuses.map(String)}
      onChange={(_name, statuses) => onChange({ ...value, statuses: statuses.map(Number) })}
      disabled={disabled}
      clearable
      size="md"
      style={vertical ? undefined : CONTROL_STYLE}
    />
  );

  if (vertical) {
    return (
      <Stack gap="sm">
        {kindControl}
        {channelControl}
        {statusControl}
      </Stack>
    );
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {kindControl}
      {channelControl}
      {statusControl}
    </Group>
  );
};

export const RemindersFilterBar = React.memo(RemindersFilterBarComponent);
